"use client";

import { type FormEvent, useState } from "react";
import { Lock } from "lucide-react";

import { updatePassword } from "@/lib/api/business-api";
import { usePageTranslation } from "@/hooks/usePageTranslation";
import { FormSanitizer } from "@/lib/utils/form-sanitizer";

interface ChangePasswordFormProps {
  userData: any;
  onSuccess: () => void;
  onCancel: () => void;
}

const headingFontStyle = { fontFamily: "var(--brand-heading-font, inherit)" };

export function ChangePasswordForm({
  userData: _userData,
  onSuccess,
  onCancel,
}: ChangePasswordFormProps) {
  const { t } = usePageTranslation();
  const [formData, setFormData] = useState({
    currentPassword: "",
    newPassword: "",
    newPasswordConfirmation: "",
  });
  const [status, setStatus] = useState<
    "idle" | "loading" | "success" | "error"
  >("idle");
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [validationErrors, setValidationErrors] = useState<
    Record<string, string>
  >({});

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    // Current password validation
    if (!formData.currentPassword) {
      errors.currentPassword = t("account.currentPasswordRequired");
    }

    // New password validation
    if (!formData.newPassword) {
      errors.newPassword = t("account.newPasswordRequired");
    } else if (formData.newPassword.length < 8) {
      errors.newPassword = t("account.newPasswordMin");
    }

    // Password confirmation validation
    if (!formData.newPasswordConfirmation) {
      errors.newPasswordConfirmation = t("auth.errConfirmPasswordRequired");
    } else if (formData.newPassword !== formData.newPasswordConfirmation) {
      errors.newPasswordConfirmation = t("auth.errPasswordsMismatch");
    }

    // Check if new password is same as current
    if (
      formData.currentPassword &&
      formData.newPassword &&
      formData.currentPassword === formData.newPassword
    ) {
      errors.newPassword = t("account.newPasswordSame");
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setErrorMessage("");
    setValidationErrors({});

    if (!validateForm()) {
      return;
    }

    // SECURITY: Validate current password
    const currentPasswordResult = FormSanitizer.validatePassword(formData.currentPassword, { minLength: 1 });
    if (!currentPasswordResult.isValid) {
      setErrorMessage(
        currentPasswordResult.error || t("account.invalidCurrentPassword")
      );
      return;
    }

    // SECURITY: Validate new password
    const newPasswordResult = FormSanitizer.validatePassword(formData.newPassword, {
      minLength: 8,
      requireUppercase: false,
      requireLowercase: false,
      requireNumbers: false,
      requireSpecialChars: false,
    });
    if (!newPasswordResult.isValid) {
      setErrorMessage(
        newPasswordResult.error || t("account.invalidNewPassword")
      );
      return;
    }

    setStatus("loading");

    try {
      // Get access token from storage (same as EditProfileForm)
      const accessToken = localStorage.getItem("auth_token") || sessionStorage.getItem("auth_token");
      if (!accessToken) {
        throw new Error(t("account.noAccessToken"));
      }

      // Prepare data for backend - only send required fields in camelCase
      const passwordData = {
        currentPassword: formData.currentPassword,
        newPassword: formData.newPassword,
      };

      const response = await updatePassword(accessToken, passwordData);

      if (response.status) {
        setStatus("success");
        // Reset form
        setFormData({
          currentPassword: "",
          newPassword: "",
          newPasswordConfirmation: "",
        });
        // Call onSuccess after showing success message
        setTimeout(() => {
          onSuccess();
        }, 2000);
      } else {
        setStatus("error");
        setErrorMessage(response.message || t("account.passwordUpdateFailed"));
      }
    } catch (error: any) {
      setStatus("error");
      setErrorMessage(
        error.response?.data?.message ||
          error.message ||
          t("auth.genericError")
      );
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    // Clear validation error for this field
    if (validationErrors[name]) {
      setValidationErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const passwordInputClass = (hasError: boolean) =>
    `w-full rounded-brand border px-4 py-3 text-brand-fg transition-all focus:border-brand-primary focus:ring-2 focus:ring-brand-ring ${
      hasError ? "border-brand-danger" : "border-brand-border"
    }`;

  return (
    <div className="change-password-form">
      <div className="mb-6 flex items-center space-x-3">
        <div className="rounded-brand bg-brand-primary/10 p-3">
          <Lock className="h-6 w-6 text-brand-primary" />
        </div>
        <h2
          className="text-2xl font-bold text-brand-fg"
          style={headingFontStyle}
        >
          {t("account.changePassword")}
        </h2>
      </div>

      <div className="mb-6 rounded-brand border border-brand-primary/30 bg-brand-primary/5 p-4">
        <p className="text-sm text-brand-fg">
          <strong>{t("account.securityTipTitle")}</strong>{" "}
          {t("account.securityTipBody")}
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Current Password Field */}
        <div className="form-group">
          <label
            htmlFor="currentPassword"
            className="mb-2 block text-sm font-medium text-brand-fg"
          >
            {t("account.currentPassword")}
          </label>
          <input
            type="password"
            id="currentPassword"
            name="currentPassword"
            value={formData.currentPassword}
            onChange={handleChange}
            required
            className={passwordInputClass(!!validationErrors.currentPassword)}
            placeholder={t("account.currentPasswordPlaceholder")}
            disabled={status === "loading"}
          />
          {validationErrors.currentPassword && (
            <p className="mt-1 text-sm text-brand-danger">
              {validationErrors.currentPassword}
            </p>
          )}
        </div>

        {/* New Password Field */}
        <div className="form-group">
          <label
            htmlFor="newPassword"
            className="mb-2 block text-sm font-medium text-brand-fg"
          >
            {t("auth.newPasswordLabel")}
          </label>
          <input
            type="password"
            id="newPassword"
            name="newPassword"
            value={formData.newPassword}
            onChange={handleChange}
            required
            className={passwordInputClass(!!validationErrors.newPassword)}
            placeholder={t("auth.newPasswordPlaceholder")}
            disabled={status === "loading"}
          />
          {validationErrors.newPassword && (
            <p className="mt-1 text-sm text-brand-danger">
              {validationErrors.newPassword}
            </p>
          )}
          <p className="mt-1 text-xs text-brand-muted">
            {t("auth.passwordHint")}
          </p>
        </div>

        {/* Confirm New Password Field */}
        <div className="form-group">
          <label
            htmlFor="newPasswordConfirmation"
            className="mb-2 block text-sm font-medium text-brand-fg"
          >
            {t("account.confirmNewPasswordLabel")}
          </label>
          <input
            type="password"
            id="newPasswordConfirmation"
            name="newPasswordConfirmation"
            value={formData.newPasswordConfirmation}
            onChange={handleChange}
            required
            className={passwordInputClass(
              !!validationErrors.newPasswordConfirmation
            )}
            placeholder={t("auth.confirmNewPasswordPlaceholder")}
            disabled={status === "loading"}
          />
          {validationErrors.newPasswordConfirmation && (
            <p className="mt-1 text-sm text-brand-danger">
              {validationErrors.newPasswordConfirmation}
            </p>
          )}
        </div>

        {/* Error Message */}
        {status === "error" && errorMessage && (
          <div className="rounded-brand border border-brand-danger/30 bg-brand-danger/10 p-4">
            <p className="text-sm text-brand-danger">{errorMessage}</p>
          </div>
        )}

        {/* Success Message */}
        {status === "success" && (
          <div className="rounded-brand border border-brand-success/30 bg-brand-success/10 p-4">
            <p className="text-sm text-brand-success">
              {t("account.passwordUpdated")}
            </p>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex items-center space-x-4">
          <button
            type="submit"
            disabled={status === "loading"}
            className={`flex-1 rounded-brand px-4 py-3 font-semibold text-brand-primary-foreground transition-all ${
              status === "loading"
                ? "cursor-not-allowed bg-gray-400"
                : "bg-brand-primary hover:bg-brand-primary-hover"
            }`}
          >
            {status === "loading" ? (
              <span className="flex items-center justify-center">
                <svg
                  className="me-3 -ms-1 h-5 w-5 animate-spin text-current"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                {t("account.updating")}
              </span>
            ) : (
              t("account.changePassword")
            )}
          </button>

          <button
            type="button"
            onClick={onCancel}
            disabled={status === "loading"}
            className="rounded-brand border border-brand-border px-6 py-3 font-semibold text-brand-fg transition-colors hover:bg-brand-fg/5 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {t("common.cancel")}
          </button>
        </div>
      </form>
    </div>
  );
}
